// 第三方库
import { makeObservable, observable, computed, action, runInAction, toJS } from 'mobx';

// 内部模块 - Store
import erpDatabaseStore from './ErpDatabaseStore';
import { userStore } from './UserStore';
import { serviceFactory } from './factories/ServiceFactory';
import { chatUserPickerStore } from './ChatUserPickerStore';

// 内部模块 - 常量
import { DEFAULT_PAGE_SIZE } from '../constants/constants';

// 内部模块 - 工具
import { logger } from '../utils/logger';
import { getTenantIdFromStorage } from '../utils/envUtils';

/**
 * 服务存储类 - 重构后
 * 遵循 SOLID 原则，职责单一，易于扩展
 */
class ServiceStore {
  constructor() {
    // 核心数据
    this._tableData = null;
    this._viewData = null;
    this._error = null;
    this._chatUserIds = [];

    // 定时器管理 - 已删除，改为SSE推送

    // 通过工厂获取服务依赖
    this._viewManagementService = serviceFactory.getViewManagementService();
    this._unifiedQueryService = serviceFactory.getUnifiedQueryService(erpDatabaseStore);
    this._userSelectionService = serviceFactory.getUserSelectionService();

    makeObservable(this, {
      // 状态
      _tableData: observable,
      _viewData: observable,
      _error: observable,
      _chatUserIds: observable,
      // 定时器相关属性已删除

      // 计算属性
      tableData: computed,
      viewData: computed,
      selectedViewId: computed,
      chatUserIds: computed,
      userContext: computed,
      userAvatars: computed,
      isInitializing: computed,
      initializationComplete: computed,
      isViewLoading: computed,
      viewError: computed,

      // 动作
      setTableData: action,
      setViewData: action,
      setError: action,
      setChatUserIds: action,
      initializeData: action,
      fetchViewData: action,
      handleViewClick: action,
      // 定时器相关方法已删除
    });

    // 设置视图变化监听器
    this._setupViewChangeListener();
  }

  // ==================== 计算属性 ====================
  get tableData() {
    return this._tableData;
  }

  get viewData() {
    return this._viewData;
  }

  get selectedViewId() {
    return this._viewManagementService.selectedViewId;
  }

  get chatUserIds() {
    return toJS(this._chatUserIds);
  }

  get unifiedQueryService() {
    return this._unifiedQueryService;
  }

  get userContext() {
    const chatUserTable = erpDatabaseStore.chatUserTableDetail?.data;
    if (!chatUserTable?.columns) return [];

    return chatUserTable.columns.map(({ id, name, columnType }) => ({
      value: id,
      label: name,
      type: columnType || 'string',
    }));
  }

  get userAvatars() {
    return userStore.users;
  }

  get isInitializing() {
    return this._unifiedQueryService.isInitializing;
  }

  get initializationComplete() {
    return this._unifiedQueryService.initializationComplete;
  }

  get isViewLoading() {
    return erpDatabaseStore.isViewLoading;
  }

  get viewError() {
    return erpDatabaseStore.viewError;
  }

  // ==================== 动作方法 ====================
  setTableData(data) {
    this._tableData = data;
  }

  setViewData(data) {
    this._viewData = data;
  }

  setError(error) {
    this._error = error;
  }

  setChatUserIds(ids) {
    this._chatUserIds = ids;
  }

  /**
   * 初始化数据 - 优化为一次性获取，应用租户隔离原则
   * @returns {Promise<void>}
   */
  async initializeData() {
    // 确保租户上下文存在
    const tenantId = getTenantIdFromStorage();
    if (!tenantId || tenantId === '0') {
      throw new Error('租户ID缺失，无法初始化数据');
    }

    logger.info('[ServiceStore] ===== 开始初始化数据 =====');
    logger.info('[ServiceStore] 租户ID:', { tenantId });

    // 一次性获取所有必要数据，不再依赖定时器
    await this._unifiedQueryService.initializeData(
      this.setTableData.bind(this),
      this.setError.bind(this)
    );

    logger.info('[ServiceStore] 数据初始化完成');
    logger.info('[ServiceStore] 初始化后状态:', {
      selectedViewId: this.selectedViewId,
      hasTableData: !!this.tableData,
      hasViews: !!this.tableData?.views?.length,
      viewCount: this.tableData?.views?.length || 0,
    });
    logger.info('[ServiceStore] ===== 数据初始化完成 =====');
  }

  /**
   * 获取视图数据
   * @param {Object} request - 请求参数
   * @param {Object} config - 配置参数
   * @returns {Promise<Object>} 视图数据
   */
  async fetchViewData(request, config = {}) {
    logger.info('[ServiceStore] ===== 开始获取视图数据 =====');
    logger.info('[ServiceStore] 请求参数:', {
      viewId: request.viewId,
      page: request.page,
      sort: request.sort,
    });

    const data = await erpDatabaseStore.fetchViewData(request, config);

    logger.info('[ServiceStore] 获取到数据:', {
      hasData: !!data,
      dataLength: data?.data?.length || 0,
      total: data?.total || 0,
    });

    runInAction(() => {
      // 检查是否是分页请求（第二页及以后）
      const isPaginationRequest = request?.page?.current > 1;

      if (isPaginationRequest && this._viewData?.data && data?.data) {
        // 分页请求：合并数据而不是替换
        const mergedData = {
          ...data,
          data: [...this._viewData.data, ...data.data],
        };
        logger.info('[ServiceStore] 分页数据合并', {
          currentPage: request.page.current,
          existingDataLength: this._viewData.data.length,
          newDataLength: data.data.length,
          mergedDataLength: mergedData.data.length,
          total: data.total,
        });
        this.setViewData(mergedData);
      } else {
        // 第一页或非分页请求：直接替换
        // 修复：确保即使API返回空数据也要设置到store中
        logger.info('[ServiceStore] 设置视图数据');
        this.setViewData(data);
      }

      this.setError(null);

      // 修复：即使没有数据也要设置空的chatUserIds数组
      if (data?.data && data.data.length > 0) {
        const chatUserIds = data.data.map(item => item.chat_user_id);
        this.setChatUserIds(chatUserIds);
      } else {
        // 设置空的用户ID数组，避免undefined
        this.setChatUserIds([]);
      }
    });

    logger.info('[ServiceStore] 视图数据设置完成');
    logger.info('[ServiceStore] ===== 获取视图数据完成 =====');
    return data;
  }

  /**
   * 处理视图点击
   * @param {string} viewId - 视图ID
   */
  async handleViewClick(viewId) {
    try {
      if (viewId === this.selectedViewId) {
        return;
      }

      // 清除跨视图的用户选择
      this._userSelectionService.clearCrossViewUserSelection(viewId);

      // 设置新视图并获取数据
      this._viewManagementService.setSelectedViewId(viewId);

      // 同步设置 UnifiedQueryService 的视图ID
      this._unifiedQueryService.setCurrentViewId(viewId);

      const request = {
        page: { current: 1, pageSize: DEFAULT_PAGE_SIZE },
        viewId: viewId,
        sort: { last_receive_time: 'desc' },
      };
      await this.fetchViewData(request);
    } catch (error) {
      logger.error('[ServiceStore] 处理视图点击失败:', error);
    }
  }

  // ==================== 视图管理代理方法 ====================
  addViewChangeListener(listener) {
    this._viewManagementService.addViewChangeListener(listener);
  }

  removeViewChangeListener(listener) {
    this._viewManagementService.removeViewChangeListener(listener);
  }

  isViewExists(viewId) {
    return this._viewManagementService.isViewExists(viewId, this._tableData?.views);
  }

  /**
   * 设置视图变化监听器
   */
  _setupViewChangeListener() {
    // 监听视图变化，通知 UserListTimerStore
    this._viewManagementService.addViewChangeListener(async (newViewId, oldViewId) => {
      try {
        if (newViewId && newViewId !== oldViewId) {
          logger.debug('[ServiceStore] 检测到视图变化，通知定时器', { newViewId, oldViewId });

          // 同步设置 UnifiedQueryService 的 currentViewId
          this._unifiedQueryService.setCurrentViewId(newViewId);
          logger.debug(
            '[ServiceStore] 已同步设置 UnifiedQueryService 的 currentViewId:',
            newViewId
          );

          // 优化：视图切换时立即清空会话状态
          if (chatUserPickerStore && typeof chatUserPickerStore.clearSessionState === 'function') {
            chatUserPickerStore.clearSessionState().catch(error => {
              logger.error('[ServiceStore] 清空会话状态失败:', error);
            });
          }

          // 使用ViewUserSelectionManager清除过期选择
          try {
            const { viewUserSelectionManager } = await import(
              './services/ViewUserSelectionManager'
            );
            if (
              viewUserSelectionManager &&
              typeof viewUserSelectionManager.clearViewUserSelection === 'function'
            ) {
              // 清除旧视图的用户选择
              if (oldViewId) {
                viewUserSelectionManager.clearViewUserSelection(oldViewId);
              }
            }
          } catch (importError) {
            logger.debug('[ServiceStore] 无法清除过期选择:', importError);
          }

          // 延迟导入并通知 UserListTimerStore
          const { userListTimerStore } = await import(
            './chat-user-picker-stores/UserListTimerStore'
          );
          if (userListTimerStore) {
            // 新版本：只在真正的视图切换时才停止定时器，初始化阶段不停止
            if (oldViewId) {
              // 真正的视图切换：停止当前定时器
              logger.debug('[ServiceStore] 检测到真正的视图切换，停止当前定时器');
              userListTimerStore._handleViewIdChange(newViewId);
            } else {
              // 初始化阶段：不停止定时器，让初始化完成
              logger.debug('[ServiceStore] 检测到初始化阶段的视图设置，不停止定时器');
            }
          }
        }
      } catch (error) {
        logger.error('[ServiceStore] 处理视图变化通知失败:', error);
      }
    });
  }
}

export const serviceStore = new ServiceStore();
